import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Mail } from "lucide-react";

const API = "/api";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  return String(detail);
}

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
      toast.success("Reset link sent to your email");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-6 py-12 animate-fade-in" data-testid="forgot-password-page">
      <Card className="w-full max-w-md border-border rounded-sm">
        <CardHeader className="space-y-1">
          <CardTitle className="font-heading text-2xl font-bold tracking-tight" data-testid="forgot-password-title">
            Reset your password
          </CardTitle>
          <CardDescription>
            {sent ? "Check your email for the reset link" : "Enter your email and we'll send you a reset link"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {sent ? (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-sm" data-testid="reset-link-sent">
                <div className="flex items-center gap-2 mb-2">
                  <Mail className="h-4 w-4 text-emerald-600" />
                  <p className="text-sm font-medium text-emerald-700">Reset email sent</p>
                </div>
                <p className="text-sm text-emerald-600">
                  If an account with that email exists, a password reset link has been sent to your inbox.
                </p>
              </div>
              <Link to="/login">
                <Button variant="outline" className="w-full rounded-sm" data-testid="back-to-login-btn">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
                </Button>
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="p-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded-sm" data-testid="forgot-password-error">
                  {error}
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="forgot-email">Email</Label>
                <Input
                  id="forgot-email"
                  type="email"
                  placeholder="name@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="forgot-password-email-input"
                />
              </div>
              <Button type="submit" className="w-full rounded-sm" disabled={loading} data-testid="forgot-password-submit-btn">
                {loading ? "Sending..." : "Send Reset Link"}
              </Button>
              <Link to="/login" className="block text-center">
                <Button variant="ghost" type="button" className="text-sm text-muted-foreground">
                  <ArrowLeft className="mr-2 h-4 w-4" /> Back to Login
                </Button>
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
